import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { AutocompleteWithControlsConfig } from '@shared/modules/autocomplete-with-controls';
import { AbstractFormContainer } from '@shared/classes/abstract-form-container';
import { DEF_SEAT, OPTIONS, ROW_COST_INPUT_CONFIG } from './delivery-pallets.constants';
import { OptionsSeatInput, PalletSeat } from './delivery-pallets.models';
import {
  buildOptionsSeatPayload,
  comparePallets,
  mapOptionsSeatInputToSeats,
  rowCostValidators,
} from '@shared/components/delivery-pallets/delivery-pallets.helpers';
import { areCostsEqual, distributeCostEvenly, isNullish, roundToPrecision } from '@shared/utils';
import { DeliveryPalletsControlNames } from '@shared/components/delivery-pallets/delivery-pallets.enums';
import { COST_INPUT_CONFIG } from '@shared/components/delivery-cost/delivery-cost.constants';
import { totalCostSumValidator } from '@shared/components/delivery-cost/delivery-cost.helpers';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-delivery-pallets',
  templateUrl: './delivery-pallets.component.html',
  styleUrls: ['./delivery-pallets.component.scss'],
})
export class DeliveryPalletsComponent extends AbstractFormContainer implements OnInit, OnDestroy {
  protected readonly ROW_COST_INPUT_CONFIG = ROW_COST_INPUT_CONFIG;
  protected readonly comparePallets = comparePallets;

  @Input() controlName: string = DeliveryPalletsControlNames.OptionsSeat;
  @Input() isTemplate: boolean = false;
  @Input() invoiceId: string = '';
  @Input()
  configDescription: AutocompleteWithControlsConfig = {
    ariaLabel: 'Параметри палет',
    placeholder: 'Параметри палет',
    arrow: true,
  };

  defSeat: PalletSeat = DEF_SEAT;
  seats: PalletSeat[] = [DEF_SEAT];
  seatsAmount: number = 0;
  optionsSeats: Record<string, string>[] = [];
  options: Record<string, string>[] = OPTIONS;

  constructor(protected formBuilder: UntypedFormBuilder) {
    super();
  }

  get totalCost(): UntypedFormControl {
    return this.form.get('Cost') as UntypedFormControl;
  }

  get rowCost(): UntypedFormArray {
    return this.form.get(DeliveryPalletsControlNames.RowCost) as UntypedFormArray;
  }

  get isMultiPlace(): boolean {
    return this.seats.length > 1;
  }

  @Input()
  set optionsSeat(opts: OptionsSeatInput) {
    if (!opts) {
      return;
    }
    this.seats = mapOptionsSeatInputToSeats(opts, this.options);
    // opts can arrive after addFormControls already built RowCost for the initial (default)
    // seats - e.g. once the invoice finishes loading asynchronously. Rebuild it to match the
    // new seats/costs so the FormArray length never falls out of sync with the *ngFor.
    if (this.form?.get(DeliveryPalletsControlNames.RowCost)) {
      this.form.setControl(
        DeliveryPalletsControlNames.RowCost,
        this.formBuilder.array(this.seats.map((seat) => this.formBuilder.control(seat.cost || ''))),
      );
      this.changeWatcher();
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.recalculatePlacesCost();
  }

  // Rebuilds the flat OptionsSeat payload (one entry per physical pallet, dimensions
  // duplicated by amount, cost = row cost split evenly across that row's pallets) and
  // syncs SeatsAmount/OptionsSeat/RowCost form controls from the current seats/rowCost state.
  changeWatcher(): void {
    if (this.seats) {
      const getRowCost = (index: number): string =>
        this.isMultiPlace ? this.rowCost?.at(index)?.value : this.rowCost?.at(0)?.value;
      const res = buildOptionsSeatPayload(this.seats, getRowCost);
      this.optionsSeats = res.options;
      this.seatsAmount = res.amount;
    } else {
      this.optionsSeats = [];
      this.seatsAmount = 0;
    }
    this.form.get(DeliveryPalletsControlNames.SeatsAmount).setValue(this.seatsAmount);
    this.form.get(DeliveryPalletsControlNames.OptionsSeat).setValue(this.optionsSeats);
    this.recalculatePlacesCost();
  }

  optionChange(val: Record<string, string>, seat: PalletSeat): void {
    seat.option = val;
    this.changeWatcher();
  }

  addOptionsSeat(): void {
    // Going from 1 row to 2 always redistributes: in single-row mode the row's own cost is
    // whatever totalCost is - the field the user actually filled in - so that's what must be
    // split. From 2+ rows on, the per-row cost inputs are visible and user-editable, so only
    // redistribute if the user hasn't already diverged them.
    const wasSingleSeat = this.seats.length === 1;
    const existingCosts = this.rowCost.controls.map((control) => control.value);
    const shouldDistributeCost = wasSingleSeat || areCostsEqual(existingCosts);

    this.seats.push({ ...this.defSeat });
    this.rowCost.push(this.formBuilder.control(''));

    if (shouldDistributeCost) {
      const distributedCosts = distributeCostEvenly(this.totalCost.value, this.rowCost.length);
      distributedCosts.forEach((cost, index) => this.rowCost.at(index).setValue(cost, { emitEvent: false }));
    }

    this.changeWatcher();
  }

  removeOptionsSeat(index: number): void {
    if (this.isMultiPlace) {
      this.seats.splice(index, 1);
      this.rowCost.removeAt(index);
      this.changeWatcher();
    }
  }

  addFormControls(): void {
    this.form.addControl(DeliveryPalletsControlNames.OptionsSeat, new UntypedFormControl(''));
    this.form.addControl(
      DeliveryPalletsControlNames.SeatsAmount,
      new UntypedFormControl('', [Validators.required, Validators.min(1)]),
    );
    this.form.addControl(
      DeliveryPalletsControlNames.RowCost,
      this.formBuilder.array(this.seats.map((seat) => this.formBuilder.control(seat.cost || ''))),
    );
  }

  ngOnDestroy(): void {
    this.form.removeControl(DeliveryPalletsControlNames.OptionsSeat);
    this.form.removeControl(DeliveryPalletsControlNames.SeatsAmount);
    this.form.removeControl(DeliveryPalletsControlNames.RowCost);
    this.totalCost?.enable({ emitEvent: false });
  }

  private recalculatePlacesCost(): void {
    if (!this.totalCost || !this.rowCost) {
      return;
    }

    this.syncRowCostValidators();
    this.syncTotalCost();
  }

  private syncRowCostValidators(): void {
    this.rowCost.controls.forEach((control) => {
      control.setValidators(
        this.isMultiPlace
          ? [
              rowCostValidators(this.isTemplate),
              totalCostSumValidator(() => this.getRowCostTotal(), COST_INPUT_CONFIG.maxValue),
            ]
          : [],
      );
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private getRowCostTotal(): number {
    return this.rowCost.controls.reduce((acc, control) => acc + (Number(control.value) || 0), 0);
  }

  // totalCost is set with onlySelf so its own valueChanges still fires (delivery-cost.component
  // relies on it to re-check the service cost), without propagating to the whole stepDeliveryForm.
  private syncTotalCost(): void {
    if (this.isMultiPlace) {
      const total = this.getRowCostTotal();
      this.totalCost.setValue(total ? roundToPrecision(total, 2) : '', { onlySelf: true });
      this.totalCost.disable({ emitEvent: false });
      return;
    }

    const soleCost = this.rowCost.at(0)?.value;
    if (!isNullish(soleCost)) {
      this.totalCost.setValue(soleCost, { onlySelf: true });
    }
    this.totalCost.enable({ emitEvent: false });
  }
}
